import Icon from '@components/v2/ui/Icon/Icon';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import classNames from 'classnames';
import { ArrowRight, Star, X } from 'lucide-react';
import { MouseEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import useMoney from '../../hooks/useMoney';
import LangEnum from '../../lang/LangEnum';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import useProductActionsStore from '../../stores/ProductActionsStore/productActions.store';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { OrderView } from '../orders/ordersView.types';
import { useTranslation } from '../translation/TranslationContext';
import useOrderBook from './hooks/useOrderBook';
import { useOrderBookCardStyles } from './styles/OrderBookCardStyles';
import { IOrderBookCardProps } from './types/orderBook.types';
import { getOrderCardSummary } from './utils/formatters';

const OrderBookCard = ({ activeOrder, order, testId }: IOrderBookCardProps) => {
  const isSuggestedOrder = order?.status === 'SUGGESTED';
  const { classes } = useOrderBookCardStyles({ activeOrder, isSuggestedOrder });
  const { onEdit, closeOrderBook } = useOrderBook();
  const history = useHistory();
  const { translate, locale } = useTranslation();
  const { team } = useApplicationContext();
  const { formatMoneyWithoutCents } = useMoney();
  const [
    handleChangeSelectedOrder,
    selectedOrder,
    selectedOrderSummary,
    handleRemoveOrder,
    isBlockOrderBook,
    loadingOrderItems,
    orderProductsIds,
    otherOrdersProductsIds,
  ] = useOrderBookStore(
    useShallow((state) => [
      state.handleChangeSelectedOrder,
      state.selectedOrder,
      state.selectedOrderSummary,
      state.handleRemoveOrder,
      state.isBlockOrderBook,
      state.loadingOrderItems,
      state.orderProductsIds,
      state.otherOrdersProductsIds,
    ]),
  );
  const closeProductNotification = useProductActionsStore(
    (state) => state.closeProductNotification,
  );
  const isSelectedOrder = selectedOrder?.id === order?.id;
  const orderProductsInfo = otherOrdersProductsIds?.find((info) => info.orderId === order?.id);
  const cardIsBlocked = (isBlockOrderBook || loadingOrderItems) && !activeOrder;

  const { items, pieces, totalValue } = getOrderCardSummary({
    order,
    isSelectedOrder,
    selectedOrder,
    selectedOrderSummary,
    orderProductsIds,
    orderProductsInfo,
    translate,
    locale,
  });

  const handleRedirectToActiveOrder = () => {
    history.push(`/orders/${selectedOrder?.id}/${OrderView.EDIT}`);
    closeOrderBook();
  };

  const handleRedirectToNewActiveOrder = () => {
    if (!onEdit) {
      return;
    }
    history.push(`/orders/${order?.id}/${OrderView.EDIT}`);
  };

  const handleRemoveFromBook = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (cardIsBlocked || !order) {
      return;
    }
    handleRemoveOrder(order.id, team?.id);
  };

  const handleClickCard = () => {
    if (cardIsBlocked || !order) {
      return;
    }

    if (activeOrder) {
      handleRedirectToActiveOrder();
      return;
    }

    handleChangeSelectedOrder({
      teamId: team?.id,
      translate,
      order,
    });

    closeProductNotification();
    handleRedirectToNewActiveOrder();
  };

  return (
    <Box
      className={classes.card}
      test-id={testId}
      sx={{ opacity: cardIsBlocked ? 0.2 : 1 }}
      onClick={handleClickCard}
    >
      <Box className={classes.codeContainer}>
        <Box
          sx={{
            display: 'flex',
            gap: (theme) => theme.spacing(1),
            alignItems: 'center',
          }}
        >
          {isSuggestedOrder && <Icon icon={Star} size="xs" className={classes.starIcon} />}
          <Typography variant="body2" color="textSecondary" className={classes.activeText}>
            {order?.code}
          </Typography>
        </Box>
        <Typography
          noWrap
          variant="body2"
          color="textSecondary"
          className={classNames(classes.customerName, classes.activeText)}
        >
          {order?.customer?.commercialName ?? translate(LangEnum.GLOBAL_NO_CLIENT)}
        </Typography>
        <Typography noWrap variant="body2" color="textSecondary" className={classes.activeText}>
          {order?.customer?.companyName ?? '-'}
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            rowGap: '2px',
            display: 'flex',
            overflow: 'hidden',
            flexDirection: 'column',
          }}
        >
          <Typography variant="body2" color="textSecondary" className={classes.valueText}>
            {items.formattedValue}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.valueText}>
            {pieces.label}
          </Typography>
          <Typography variant="body2" color="textSecondary" className={classes.valueText}>
            {formatMoneyWithoutCents(Number(totalValue || 0))}
          </Typography>
        </Box>
        {activeOrder ? (
          <Box className={classes.iconBox}>
            <Icon icon={ArrowRight} size="lg" className={classes.actionIcon} />
          </Box>
        ) : (
          <Box
            role="button"
            className={classes.iconBox}
            aria-label={translate(LangEnum.ORDER_BOOK_REMOVE_BOOK)}
            onClick={handleRemoveFromBook}
          >
            <Icon icon={X} size="lg" className={classes.actionIcon} />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default OrderBookCard;
